import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import '../styles/ActivityFeed.css';
import {
    StickyNote,
    Mail as Email,
    LucideScrollText as All,
    Printer,
    Phone,
    MessageSquare,
    CheckSquare,
    AlarmClock,
    ArrowUp,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    EllipsisVertical,
    Ellipsis,
    Pin,
    PinOff,
    Check,
    CheckCheck,
    Expand,
    Maximize,
    Maximize2,
    Minimize2,
    X,
    FileSignature,
    Download,
    LoaderCircle,
    ChevronDown,
  } from "lucide-react";
import { SearchBar } from "./Nav";
import { AddActivity, Boolean as BooleanField, DateInput, File, MentionsTextarea, MultiFile, Notification, SearchSelect } from "./FieldComponents";
import { CreateContact } from "./CreateContact";
import Modal from "./Modal";
import { PinOutlined } from "@mui/icons-material";
import { file } from "jszip";
import { useNavigate, useLocation } from "react-router";
import { useOptimizedResize } from '../utils/performance';
import { fetchNavSearchResults, toCaseSelectionOptions } from '../utils/navSearch';
import fuzzysort from 'fuzzysort';
import { set } from "date-fns";
import { Spinner } from "@mynaui/icons-react";
import { formatCommunicationStatus } from '../utils/communicationStatus';
import { EntityHoverPreview } from './EntityHoverPreview';
import {
    applyActivityScopeChanges,
    canCommitActivityLiveSnapshot,
    mergeActivityFeedChanges,
    mergeActivityFeedPage,
} from '../utils/liveDataReconciliation';
import {
    activityFeedLiveResponseRequiresResync,
    createActivityFeedLivePollGate,
    requestActivityFeedLivePage,
} from '../utils/activityFeedLivePolling';
import { caseDbApiEndpoint } from '../utils/apiOrigin';

export const ACTIVITY_FEED_LIVE_POLL_MS = 15000;
export const ACTIVITY_FEED_NEW_MARKER_MS = 60000;

const containsHtmlTags = (content = '') => /<\/?[a-z][\s\S]*>/i.test(String(content));

const escapeHtml = (content = '') => String(content)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const normalizeEmailNewlines = (content = '') => String(content)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n');

const formatEmailContentForDisplay = (content = '', transformHtml = (html) => html) => {
    const normalizedContent = normalizeEmailNewlines(content);
    const isHtml = containsHtmlTags(normalizedContent);

    return {
        html: transformHtml(isHtml ? normalizedContent : escapeHtml(normalizedContent)),
        preserveWhitespace: !isHtml,
    };
};

const ActivityFormResponse = ({ data, userId, fetchFeed }) => {
    const metadata = data?.metadata || {};
    const responses = metadata?.responses && typeof metadata.responses === 'object' ? metadata.responses : {};
    const currentUserResponse = responses?.[String(userId)] || null;
    const submittedReply = Array.isArray(data?.replies)
        ? data.replies.find(reply => {
            const formResponse = reply?.metadata?.form_response;
            return Number(reply?.author_id) === Number(userId)
                && formResponse
                && Number(formResponse.parent_activity_id) === Number(data?.id);
        })
        : null;
    const hasSubmittedResponse = !!submittedReply || !!currentUserResponse?.reply_id;
    const currentResponse = submittedReply?.metadata?.form_response?.value ?? currentUserResponse?.value ?? 2;
    const [value, setValue] = useState(Number(currentResponse));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setValue(Number(currentResponse));
    }, [currentResponse]);

    const submitResponse = async () => {
        if (value === '' || isSubmitting) return;
        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'submit_form_response',
                    activity_id: data.id,
                    value
                })
            });
            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Failed to submit response');
            }
            fetchFeed?.();
        } catch (err) {
            setError(err.message || 'Failed to submit response');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (hasSubmittedResponse) {
        return null;
    }

    return (
        <div className='activity-form-response' onClick={(event) => event.stopPropagation()} style={{ marginTop: '12px' }}>
            <label className='subtext'>Response</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <BooleanField
                    options={['Yes', 'No', 'Unknown']}
                    value={value}
                    onChange={setValue}
                />
                <button className='action small quaternary' onClick={submitResponse} disabled={value === '' || isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Submit'}
                </button>
            </div>
            {error && <div className='subtext' style={{ marginTop: '6px', color: 'var(--priority-color)' }}>{error}</div>}
        </div>
    );
};
  
export const iconMap = {
    All: <All size={18} />,
    Notes: <StickyNote size={18} />,
    Emails: <Email size={18} />,
    Faxes: <Printer size={18} />,
    'Phone Calls': <Phone size={18} />,
    Texts: <MessageSquare size={18} />,
    Tasks: <CheckSquare size={18} />,
    Reminders: <AlarmClock size={18} />,
    Forms: <CheckSquare size={18} />,
    'eSignatures': <FileSignature size={18} />,
};

const FeedItem = ({ type, setActiveFeed, activeFeed, notifications, mobile }) => {
    let feedType = 'All';
    switch (type) {
        case 'Phone Calls':
            feedType = 'calls';
            break;
        case 'Faxes':
            feedType = 'faxes';
            break;
        case 'eSignatures':
            feedType = 'eSignatures';
            break;
        case 'Forms':
            feedType = 'form';
            break;
        default:
            feedType = type;
            break;
    }
    return (
        <div 
            className={`action quaternary feed-type`} 
            style={ activeFeed === feedType ? { backgroundColor: 'var(--fill)', fontSize: '12.8px', color: 'var(--secondary-color)', borderColor: 'var(--fill)' } : { fontSize: '12.8px', backgroundColor: 'var(--secondary-color)', borderColor: 'var(--border-color)' }}
            onMouseEnter={(e) => {
                if (activeFeed !== feedType) {
                    e.currentTarget.style.borderColor = 'var(--fill)';
                    e.currentTarget.style.color = 'var(--text-color)';
                }
            }}
            onMouseLeave={(e) => {
                if(activeFeed !== feedType) {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    e.currentTarget.style.color = 'var(--subtext-color)'
                }
            }}
            onClick={() => setActiveFeed(feedType)}
        >
            <span className='feed-type-text'>
                {iconMap[type]}{!mobile && type}
                {!mobile && type === 'Reminders' && (notifications.reminder > 0 || notifications.priority > 0) &&
                    <div className='tasks-notis'>
                        {notifications.priority > 0 && <Notification count={notifications.priority} type='priority' />}
                        {notifications.reminder > 0 && <Notification count={notifications.reminder} type='reminder' />}
                    </div>
                }
            </span>
        </div>
    );
};

const Activity = ({ data, user_id, user, fetchFeed, reminder, priority, setActiveItem, activeItem, users, isReply = false, activityKey = null, searchQuery = '', searchSpecifier = 'all', depth = 0, sections, canSyncToChildCases = false, caseId = null }) => {
    const formatLastModified = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return ["", "", "", "", "", ""];

        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        try {
            const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
            const isDateTimeWithSpace = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString);
            const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
            const isIsoWithoutZone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateString);

            let normalized = dateString;
            if (!hasTimeZone) {
                if (isDateTimeWithSpace) {
                    normalized = `${dateString.replace(' ', 'T')}Z`;
                } else if (isDateOnly) {
                    normalized = `${dateString}T00:00:00Z`;
                } else if (isIsoWithoutZone) {
                    normalized = `${dateString}Z`;
                }
            }

            let parsedDate = new Date(normalized);

            if (isNaN(parsedDate.getTime())) {
                parsedDate = new Date(`${dateString}T00:00:00Z`);
            }

            if (isNaN(parsedDate.getTime())) {
                return ["", "", "", "", "", ""];
            }

            const fallbackTimeZone = "America/Chicago";
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimeZone;
            let zonedTime;
            try {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: userTimeZone }));
            } catch (e) {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: fallbackTimeZone }));
            }

            const iso = parsedDate.toISOString();
            const longDate = `${months[zonedTime.getMonth()]} ${zonedTime.getDate()}, ${zonedTime.getFullYear()}`;
            const shortDate = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()}`;
            const shortFormatted = `${zonedTime.getDate()} ${months[zonedTime.getMonth()].slice(0, 3)} ${zonedTime.getFullYear()}`;
            const hours = zonedTime.getHours();
            const minutes = zonedTime.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const hour12 = hours % 12 || 12;
            const minutePadded = String(minutes).padStart(2, '0');
            const timeFormatted = `${hour12}:${minutePadded} ${ampm}`;
            const dateTimeFormatted = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()} ${timeFormatted}`;

            return [longDate, iso, timeFormatted, shortDate, shortFormatted, dateTimeFormatted];
        } catch (e) {
            console.error("Invalid date:", dateString, e);
            return ["", "", "", "", "", ""];
        }
    };

    const [showOptions, setShowOptions] = useState(false);
    const optionRef = useRef(null);
    const [hoveringPin, setHoveringPin] = useState(null);
    const contentRef = useRef(null);
    const [canExpand, setCanExpand] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isReplying, setIsReplying] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [selectedCases, setSelectedCases] = useState([]);
    const [availableCases, setAvailableCases] = useState([]);
    const [updatingAssignees, setUpdatingAssignees] = useState(false);
    const [generatingEmailPdf, setGeneratingEmailPdf] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [hoveringComplete, setHoveringComplete] = useState(null);
    const [replyData, setReplyData] = useState({
        content: '',
        attachments: [],
        mentions: []
    });
    const normalizeAssignees = (assignees) => {
        if (!Array.isArray(assignees)) {
            return [];
        }
        return assignees
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));
    };

    const [tempAssignees, setTempAssignees] = useState(() => {
        // Initialize with current assignees from data.deadline
        if (data?.deadline?.assignees) {
            return normalizeAssignees(data.deadline.assignees);
        }
        return data?.task ? normalizeAssignees([data.task]) : [];
    });
    const communicationStatus = formatCommunicationStatus(data);
    const [editData, setEditData] = useState({
        subject: data?.subject || '',
        content: data?.content || '',
        type: data?.type || 'notes',
        task: data?.task || '',
        due_date: data?.deadline?.due ? data.deadline.due.split(' ')[0] : '',
        attachments: [],
        mentions: (() => {
            if (Array.isArray(data?.mentions)) {
                return data?.mentions;
            }
            if (typeof data?.mentions === 'string') {
                try {
                    const parsed = JSON.parse(data?.mentions);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    return [];
                }
            }
            return [];
        })(),
        tags: (() => {
            if (Array.isArray(data?.tags)) {
                return data?.tags.join(', ');
            }
            if (typeof data?.tags === 'string') {
                try {
                    const parsed = JSON.parse(data?.tags);
                    return Array.isArray(parsed) ? parsed.join(', ') : '';
                } catch {
                    return data?.tags;
                }
            }
            return '';
        })()
    });
    const deadline = data?.deadline?.due
        ? new Date(data.deadline.due).toLocaleDateString()
        : "Not set";
    const navigate = useNavigate();

    const completeTask = async (complete = true) => {
        try {
            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: Number(data?.id),
                    done: complete === true
                }),
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const result = await response.json();

            if (result.success) {
                fetchFeed();
            } else {
                console.error('Failed to mark task as complete:', result.message);
            }
        } catch (err) {
            console.error('Error completing task:', err);
        }
    };

    const pinNote = (pin, action) => {
        try {
            fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: data.id,
                    pin: pin,
                    action: action
                }),
            })
            .then((response) => response.json())
            .then((data) => {
                if (data.success) {
                    setShowOptions(false);
                    fetchFeed();
                } else {
                    console.error("Error pinning note:", data.message);
                }
            })
            .catch((error) => {
                console.error("Error pinning note:", error);
            });
        } catch (error) {
            console.error("Error pinning note:", error);
        }
    }

    const handleDelete = async () => {
        if (!data?.id) return;
        if (!window.confirm('Delete this activity? This cannot be undone.')) return;

        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: data.id,
                    action: 'delete'
                })
            });

            const result = await response.json();
            if (result.success) {
                setShowOptions(false);
                fetchFeed();
            } else {
                console.error('Failed to delete activity:', result.message || result.error);
            }
        } catch (error) {
            console.error('Error deleting activity:', error);
        }
    };

    const handleGenerateEmailPdf = async () => {
        if (!data?.id || generatingEmailPdf) return;

        setGeneratingEmailPdf(true);
        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: data.id,
                    action: 'generate_email_pdf'
                })
            });

            if (!response.ok) {
                let errorMessage = 'Failed to generate email PDF';
                try {
                    const result = await response.json();
                    errorMessage = result?.message || result?.error || errorMessage;
                } catch (_) {
                    const text = await response.text();
                    if (text) errorMessage = text;
                }
                throw new Error(errorMessage);
            }

            const blob = await response.blob();
            const disposition = response.headers.get('Content-Disposition') || '';
            const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
            const filename = filenameMatch?.[1] || `email_activity_${data.id}.pdf`;
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            setShowOptions(false);
        } catch (error) {
            console.error('Error generating email PDF:', error);
            alert(error.message || 'Failed to generate email PDF. Please try again.');
        } finally {
            setGeneratingEmailPdf(false);
        }
    };

    // Helper to determine task completion status
    const getTaskCompletionStatus = (task, currentUserId) => {
        if (!task.deadline?.assignees) return { status: 'incomplete', userCompleted: false };
        
        const assignees = task.deadline.assignees;
        
        // Handle completion_status as either object or JSON string
        let completionStatus = task.deadline.completion_status || {};
        if (typeof completionStatus === 'string') {
            try {
                completionStatus = JSON.parse(completionStatus);
            } catch (e) {
                completionStatus = {};
            }
        }
        
        if (assignees.length === 0) {
            // Legacy single-assignee task
            return {
                status: task.deadline.done ? 'complete' : 'incomplete',
                userCompleted: !!task.deadline.done
            };
        }
        
        // Check how many assignees have completed
        let completedCount = 0;
        let userCompleted = false;
        
        for (const assigneeId of assignees) {
            if (completionStatus[assigneeId]) {
                completedCount++;
            }
            if (assigneeId === currentUserId && completionStatus[assigneeId]) {
                userCompleted = true;
            }
        }
        
        let status = 'incomplete';
        if (completedCount === assignees.length) {
            status = 'complete';
        } else if (completedCount > 0) {
            status = 'partial';
        }
        
        return { status, userCompleted, completedCount, totalCount: assignees.length };
    };

    const handleExpandClick = (event) => {
        // Don't minimize if user is selecting text
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
            return;
        }
        
        // Don't minimize if the click started as a text selection (double-click, etc.)
        if (event.detail > 1) {
            return;
        }
        
        // Don't minimize if user is clicking on interactive elements or icons
        const target = event.target;
        if (target.closest('button, a, input, textarea, select, [contenteditable], svg')) {
            return;
        }
        
        if (optionRef.current && !optionRef.current.contains(event.target)) {
            setActiveItem(activeItem === data.id ? null : data.id);
        }
    }

    useEffect(() => {
        if (Array.isArray(data?.deadline?.assignees)) {
            setTempAssignees(normalizeAssignees(data.deadline.assignees));
        } else if (data?.task) {
            setTempAssignees(normalizeAssignees([data.task]));
        } else {
            setTempAssignees([]);
        }
    }, [data?.deadline?.assignees, data?.task, data?.id]);

    useEffect(() => {
        if (!showOptions) return;

        const handleClickOutside = (e) => {
            if (optionRef.current && !optionRef.current.contains(e.target)) {
                setShowOptions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showOptions]);

    const pinData = typeof data.pinned === 'string' ? JSON.parse(data.pinned) : data.pinned;
    const isPinnedForUser = Array.isArray(pinData?.users) && pinData.users.includes(Number(user_id));
    const isPinnedForCase = pinData?.case === 1;

    const accessibleMentionUsernames = useMemo(() => {
        if (!Array.isArray(users) || users.length === 0) {
            return new Set();
        }

        return new Set(
            users
                .map((user) => String(user?.user || '').trim().toLowerCase())
                .filter(Boolean)
        );
    }, [users]);

    const formatContentWithMentions = (content) => {
        if (!content || accessibleMentionUsernames.size === 0) return content;

        const mentionPattern = /@([a-zA-Z0-9_]+)/g;

        return content.replace(mentionPattern, (match, username) => {
            if (accessibleMentionUsernames.has(String(username).toLowerCase())) {
                return `<span style="color: var(--fill);">${match}</span>`;
            }
            return match;
        });
    };

    const addForwardedSeparators = (html) => {
        if (!html || typeof html !== 'string') return html;

        const headerRegex = /(<p[^>]*>\s*(?:<span[^>]*>\s*)?(?:<b[^>]*>\s*)?From:\s*(?:<\/b>)?)/gi;
        return html.replace(headerRegex, (match, _group, offset) => {
            if (offset < 20) return match;
            const before = html.slice(Math.max(0, offset - 200), offset);
            if (before.includes('email-forward-separator')) return match;
            return `<hr class="email-forward-separator" />${match}`;
        });
    };

    // Helper function to highlight matching text
    const highlightText = (text, shouldHighlight) => {
        if (!text || !searchQuery || searchQuery.trim() === '' || !shouldHighlight) return text;
        
        // Use case-insensitive substring matching
        const lowerText = text.toLowerCase();
        const lowerQuery = searchQuery.toLowerCase();
        const index = lowerText.indexOf(lowerQuery);
        
        if (index === -1) return text;
        
        // Highlight the matched substring
        const before = text.substring(0, index);
        const match = text.substring(index, index + searchQuery.length);
        const after = text.substring(index + searchQuery.length);
        
        return before + '<span class="feed-match">' + match + '</span>' + after;
    };

    const formattedContent = data.type === 'emails' 
        ? data.content
        : formatContentWithMentions(data.content);

    // Apply highlighting based on search specifier
    const shouldHighlightSubject = searchQuery && (searchSpecifier === 'all' || searchSpecifier === 'subject');
    const shouldHighlightContent = searchQuery && (searchSpecifier === 'all' || searchSpecifier === 'content');
    
    const highlightedSubject = highlightText(data?.subject, shouldHighlightSubject);
    const highlightedContent = highlightText(formattedContent, shouldHighlightContent);
    const emailContent = data.type === 'emails'
        ? formatEmailContentForDisplay(data.content, addForwardedSeparators)
        : { html: data.content, preserveWhitespace: false };

    useEffect(() => {
        if (contentRef.current && activeItem !== data.id) {
            const hasScroll = contentRef.current.scrollHeight > contentRef.current.clientHeight;
            setCanExpand(hasScroll);
        }
    }, [formattedContent, activeItem]);

    // Auto-expand items with search matches
    useEffect(() => {
        if (searchQuery && searchQuery.trim() !== '') {
            let searchText = '';
            
            if (searchSpecifier === 'all') {
                searchText = `${data.subject ?? ''} ${data.content ?? ''}`;
            } else if (searchSpecifier === 'subject') {
                searchText = data.subject ?? '';
            } else if (searchSpecifier === 'content') {
                searchText = data.content ?? '';
            }
            
            // Use case-insensitive substring matching
            if (searchText.toLowerCase().includes(searchQuery.toLowerCase())) {
                setActiveItem(data.id);
            }
        }
    }, [searchQuery, searchSpecifier]);

    const handleEdit = async () => {
        try {
            const formData = new FormData();
            formData.append('id', Number(data?.id));
            formData.append('case_id', data?.case_id || 0);
            formData.append('edit', 'true');
            formData.append('subject', editData.subject);
            formData.append('content', editData.content);
            formData.append('type', editData.type);
            formData.append('pin', data.pinned ? JSON.stringify(data.pinned) : '[]');
            
            formData.append(
                "tags",
                JSON.stringify(
                    editData?.tags?.split(/[\s,]+/)?.map(tag => tag.trim())?.filter(tag => tag.length > 0)
                )
            );
            
            // Add assignees for tasks
            if (editData.type === 'tasks' && tempAssignees.length > 0) {
                formData.append('assignees', JSON.stringify(tempAssignees));
            }
            
            formData.append(
                "mentions",
                JSON.stringify(editData?.mentions || [])
            );
            
            if (editData.type === 'tasks') {
                formData.append('task', editData.task);
                formData.append('due_date', editData.due_date);
            }

            // Handle file attachments
            if (Array.isArray(editData.attachments)) {
                editData.attachments.forEach((file) => {
                    if (file && typeof file.name === "string" && typeof file.size === "number") {
                        formData.append("attachment[]", file);
                    }
                });
            }

            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const result = await response.json();

            if (result.success) {
                setIsEditing(false);
                setShowOptions(false);
                fetchFeed();
            } else {
                console.error('Failed to edit activity:', result.message);
            }
        } catch (err) {
            console.error('Error editing activity:', err);
        }
    };

    // Handler functions for edit form
    const handleEditInputChange = (key) => (value) => {
        setEditData(prev => ({ ...prev, [key]: value }));
    };

    const handleEditMentionsChange = (mentions) => {
        setEditData(prev => ({ ...prev, mentions }));
    };

    const handleCancelEdit = () => {
        // Reset tempAssignees to original data
        if (data?.deadline?.assignees) {
            setTempAssignees(data.deadline.assignees);
        } else {
            setTempAssignees(data?.task ? [data.task] : []);
        }
        
        setEditData({
            subject: data?.subject || '',
            content: data?.content || '',
            type: data?.type || 'notes',
            task: data?.task || '',
            due_date: data?.deadline?.due ? data.deadline.due.split(' ')[0] : '',
            attachments: [],
            mentions: (() => {
                if (Array.isArray(data?.mentions)) {
                    return data?.mentions;
                }
                if (typeof data?.mentions === 'string') {
                    try {
                        const parsed = JSON.parse(data?.mentions);
                        return Array.isArray(parsed) ? parsed : [];
                    } catch {
                        return [];
                    }
                }
                return [];
            })(),
            tags: (() => {
                if (Array.isArray(data?.tags)) {
                    return data?.tags?.join(', ');
                }
                if (typeof data?.tags === 'string') {
                    try {
                        const parsed = JSON.parse(data?.tags);
                        return Array.isArray(parsed) ? parsed.join(', ') : '';
                    } catch {
                        return data?.tags;
                    }
                }
                return '';
            })()
        });
        setIsEditing(false);
    };

    const isInheritedParentItem = !!data?.inherited_from_parent;
    const isAuthor = Number(data.author_id) === Number(user_id) || Number(data.author_id) === 12;
    const isAssigned = Array.isArray(data?.deadline?.assignees)
        ? data.deadline.assignees.map(Number).includes(Number(user_id || 12))
        : Number(data?.task) === Number(user_id || 12);
    const canEditTaskAssignees = !isInheritedParentItem && (isAuthor || isAssigned);
    const canEdit = !isInheritedParentItem && (
        ['notes', 'reminders'].includes(data.type)
            ? isAuthor
            : data.type === 'tasks' && canEditTaskAssignees
    );
    const canDelete = !isInheritedParentItem && isAuthor;
    const completionInfo = data?.task ? getTaskCompletionStatus(data, user_id) : null;

    const updateTaskAssignees = async (nextAssignees, options = {}) => {
        if (!canEditTaskAssignees || !data?.id) {
            return;
        }

        if (!Array.isArray(nextAssignees)) {
            return;
        }

        const normalizedAssignees = normalizeAssignees(nextAssignees);
        if (normalizedAssignees.length === 0) {
            // Allow clearing all assignees
        }

        try {
            setUpdatingAssignees(true);
            const formData = new FormData();
            const targetType = options.forceTaskType ? 'tasks' : data?.type;
            const existingTags = typeof data?.tags === 'string'
                ? data.tags
                : Array.isArray(data?.tags)
                    ? JSON.stringify(data.tags)
                    : '[]';
            const existingDueDate = data?.deadline?.due
                ? String(data.deadline.due).split(' ')[0]
                : '';
            const existingPostDate = data?.deadline?.post_date
                ? String(data.deadline.post_date).split(' ')[0]
                : '';

            formData.append('id', Number(data.id));
            formData.append('edit', 'true');
            if (targetType) {
                formData.append('type', targetType);
            }
            if (data?.subject) {
                formData.append('subject', data.subject);
            }
            if (data?.content) {
                formData.append('content', data.content);
            }
            formData.append('tags', existingTags);
            if (existingDueDate) {
                formData.append('due_date', existingDueDate);
            }
            if (existingPostDate) {
                formData.append('post_date', existingPostDate);
            }
            formData.append('assignees', JSON.stringify(normalizedAssignees));

            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                fetchFeed();
            } else {
                console.error('Failed to update assignees:', result.message);
            }
        } catch (err) {
            console.error('Error updating assignees:', err);
        } finally {
            setUpdatingAssignees(false);
        }
    };

    const handleReply = async () => {
        try {
            const formData = new FormData();
            formData.append('case_id', data?.case_id || 0);
            formData.append('lead_id', data?.lead_id || 0);
            formData.append('type', 'notes');
            formData.append('subject', `Re: ${data.subject}`);
            formData.append('content', replyData.content);
            formData.append('reply', data.id); // Link to parent for one-level replies
            formData.append('mentions', JSON.stringify(replyData.mentions || []));
            
            // Handle file attachments
            if (Array.isArray(replyData.attachments)) {
                replyData.attachments.forEach((file) => {
                    if (file && typeof file.name === "string" && typeof file.size === "number") {
                        formData.append("attachment[]", file);
                    }
                });
            }

            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const result = await response.json();

            if (result.success) {
                setIsReplying(false);
                setReplyData({ content: '', attachments: [], mentions: [] });
                setShowOptions(false);
                fetchFeed();
            } else {
                console.error('Failed to reply to activity:', result.message);
            }
        } catch (err) {
            console.error('Error replying to activity:', err);
        }
    };

    const handleCancelReply = () => {
        setReplyData({ content: '', attachments: [], mentions: [] });
        setIsReplying(false);
    };

    const loadCaseOptions = useCallback(async (query = '') => {
        const results = await fetchNavSearchResults(query, { type: 'cases', limit: 15 });
        return toCaseSelectionOptions(results).filter(c => parseInt(c.value, 10) !== parseInt(data.case_id, 10));
    }, [data.case_id]);

    // Handle moving activity to another case
    const handleMoveActivity = async (targetCaseId) => {
        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: data.id,
                    action: 'move',
                    target_case_id: targetCaseId
                }),
                credentials: 'include'
            });

            const result = await response.json();
            if (result.success) {
                setShowMoveModal(false);
                setShowOptions(false);
                fetchFeed(); // Refresh the feed
            } else {
                console.error('Error moving activity:', result.message);
                alert('Failed to move activity: ' + result.message);
            }
        } catch (error) {
            console.error('Error moving activity:', error);
            alert('Failed to move activity');
        }
    };

    // Handle copying activity to multiple cases
    const handleCopyActivity = async () => {
        if (selectedCases.length === 0) {
            alert('Please select at least one case to copy to');
            return;
        }

        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: data.id,
                    action: 'copy',
                    target_case_ids: selectedCases
                }),
                credentials: 'include'
            });

            const result = await response.json();
            if (result.success) {
                setShowCopyModal(false);
                setShowOptions(false);
                setSelectedCases([]);
                alert(`Activity copied to ${result.copied_count} case(s) successfully!`);
            } else {
                console.error('Error copying activity:', result.message);
                alert('Failed to copy activity: ' + result.message);
            }
        } catch (error) {
            console.error('Error copying activity:', error);
            alert('Failed to copy activity');
        }
    };

    const handleExcludeInheritedFromChild = async () => {
        if (!data?.id || !caseId || !isInheritedParentItem || !data?.can_exclude_from_child) {
            return;
        }

        if (!window.confirm('Hide this inherited parent activity from this child case?')) {
            return;
        }

        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: data.id,
                    action: 'exclude_from_child',
                    target_case_id: Number(caseId)
                }),
                credentials: 'include'
            });

            const result = await response.json();
            if (result.success) {
                setShowOptions(false);
                fetchFeed();
            } else {
                alert(result.message || 'Failed to hide inherited activity');
            }
        } catch (error) {
            console.error('Error hiding inherited activity:', error);
            alert('Failed to hide inherited activity');
        }
    };
    
    const isReferenced = activityKey === String(data.id) || activeItem === data.id;
    
    return (
        <div 
            className={`activity-feed-item feed-type-${data.type} ${isPinnedForCase ? 'pinned-case' : isPinnedForUser ? 'pinned-user' : ''} ${isEditing ? 'editing' : ''} ${isReplying ? 'replying' : ''} ${isReferenced ? 'referenced-item' : ''}`}
            id={`activity-${data.id}`}
            onClick={!isEditing && !isReplying ? handleExpandClick : undefined}
            style={{
                position: 'relative',
                zIndex: showOptions ? 100000 : 'auto'
            }}
        >
            <div className="activity-feed-item-header subtext">
                <span className="activity-feed-item-author">{data.author}</span>
                <span
                    className={`activity-feed-item-date${data.__liveNew ? ' activity-feed-item-new' : ''}`}
                    title={data.__liveNew ? formatLastModified(data.creation_date)[5] : undefined}
                >
                    {data.__liveNew ? 'New' : formatLastModified(data.creation_date)[5]}
                </span>
                {isInheritedParentItem && (
                    <EntityHoverPreview
                        entityType="case"
                        entityId={data?.parent_case_id || data?.source_case_id}
                        className="activity-feed-item-number activity-parent-case-preview"
                    >
                        From parent case
                    </EntityHoverPreview>
                )}
                {deadline != "Not set" && (
                    <div className='activity-feed-item-deadline subtext'>
                        Due: <span className={`${reminder.filter(p => p.id === data.id).length > 0 ? 'distant-reminder' : ''} ${priority.filter(p => p.id === data.id).length > 0 ? 'priority-reminder' : ''} ${completionInfo?.status === 'complete' ? 'completed' : completionInfo?.status === 'partial' ? 'partial' : ''}`}>{deadline}</span>
                        {completionInfo?.status === 'partial' && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', opacity: 0.8 }}>
                                ({completionInfo.completedCount}/{completionInfo.totalCount} completed)
                            </span>
                        )}
                    </div>
                )}
                {data?.call_log && (
                    <>
                        {`${data.call_log.caller} spoke with ${data.call_log.contact_name} on ${new Date(data.call_log.date).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: '2-digit',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        }).replace(',', ' @')}`}
                    </>
                )}
                {data.send_number && <span className='activity-feed-item-number'>{data.send_to}</span>}
            </div>
            {(() => {
                let tagsArray = [];
                if (Array.isArray(data?.tags)) {
                    tagsArray = data.tags;
                } else if (typeof data?.tags === 'string') {
                    try {
                        tagsArray = JSON.parse(data.tags);
                        if (!Array.isArray(tagsArray)) tagsArray = [];
                    } catch (e) {
                        tagsArray = [];
                    }
                }
                
                return tagsArray.length > 0 && (
                    <div className='activity-feed-tags'>
                        {tagsArray.map((tag, index) => (
                            <span key={index} className="activity-feed-tag tag">#{tag}</span>
                        ))}
                    </div>
                );
            })()}
            {isEditing ? (
                <div className="activity-edit-form" onClick={(e) => e.stopPropagation()}>
                    <div className='form-group activity'>
                        <label className='subtext'>Subject<span className='required'>*</span></label>
                        <input
                            type="text"
                            value={editData.subject}
                            onChange={(e) => handleEditInputChange("subject")(e.target.value)}
                            className="edit-subject-input"
                            placeholder="Subject"
                            disabled={!isAuthor}
                        />
                    </div>
                    <div className='form-group activity'>
                        <label className='subtext'>Content<span className='required'>*</span></label>
                        <MentionsTextarea
                            value={editData.content}
                            onChange={handleEditInputChange("content")}
                            onMentionsChange={handleEditMentionsChange}
                            users={users}
                            disabled={!isAuthor}
                        />
                    </div>
                    <div className='form-group activity'>
                        <label className='subtext'>Tags</label>
                        <input
                            type="text"
                            value={editData.tags}
                            onChange={(e) => handleEditInputChange("tags")(e.target.value)}
                            placeholder="Enter tags separated by commas"
                            disabled={!isAuthor}
                        />
                    </div>
                    <div className='form-group activity'>
                        <label className='subtext'>Attachments</label>
                        <MultiFile
                            value={editData.attachments}
                            onChange={handleEditInputChange("attachments")}
                            disable={!isAuthor}
                        />
                    </div>
                    <div className='form-group activity'>
                        <label className='subtext'>Type</label>
                        <select
                            value={editData.type}
                            onChange={(e) => handleEditInputChange("type")(e.target.value)}
                            className="default-select"
                            disabled={!isAuthor}
                        >
                            <option value="notes">Note</option>
                            <option value="tasks">Task</option>
                        </select>
                    </div>
                    {editData.type === 'tasks' && (
                        <div className='activity-task-info'>
                            <div className='form-group task-assign activity' style={{ flexDirection: 'column', gap: '8px' }}>
                                <label className='subtext'>Assigned to</label>
                                {tempAssignees.length > 0 && (
                                    <div className='activity-selected-users' style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                        {tempAssignees.map((assigneeId) => {
                                            const assigneeUser = users.find(u => u.id === assigneeId);
                                            if (!assigneeUser) return null;
                                            return (
                                                <div key={assigneeId} className={`assignees-list ${canEditTaskAssignees ? 'can-edit' : ''}`}>
                                                    {assigneeUser.name}
                                                    {canEditTaskAssignees && (
                                                        <div className='remove-assignee'>
                                                            <X size={14} onClick={() => {
                                                                setTempAssignees(prev => prev.filter(id => id !== assigneeId));
                                                            }} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <SearchSelect
                                    value={''}
                                    onChange={(val) => {
                                        if (!canEditTaskAssignees) {
                                            return;
                                        }
                                        const assigneeId = Number(val);
                                        if (assigneeId && !tempAssignees.includes(assigneeId)) {
                                            setTempAssignees(prev => [...prev, assigneeId]);
                                        }
                                    }}
                                    options={users?.reduce((acc, user) => {
                                        acc[user.id] = user.name;
                                        return acc;
                                    }, {})}
                                    placeholder="Add an assignee..."
                                    disabled={!canEditTaskAssignees}
                                />
                            </div>
                            <div className='form-group task-assign activity'>
                                <label className='subtext'>Due date</label>
                                <DateInput
                                    value={editData.due_date}
                                    onChange={handleEditInputChange("due_date")}
                                />
                            </div>
                        </div>
                    )}
                    <div className="edit-actions">
                        <button onClick={handleEdit} className="action">Save</button>
                        <button onClick={handleCancelEdit} className="action alt">Cancel</button>
                    </div>
                </div>
            ) : isReplying ? (
                <>
                    {data?.subject != "" && <div className='activity-title'>
                        <span className="activity-title-text" dangerouslySetInnerHTML={{ __html: highlightedSubject }}></span><span className='item-type-line'></span>
                    </div>}
                    <div
                        className={`activity-feed-item-content ${activeItem === data.id ? 'expanded-activity' : ''} ${canExpand && activeItem !== data.id ? 'has-more' : ''}`}
                        ref={contentRef}
                        style={(data.type === 'emails' || data.type === '') ? { whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal", wordWrap: "break-word", maxWidth: "100%", overflowWrap: "break-word", overflowX: "hidden" } : {}}
                    >
                        {data?.type === 'emails' && data?.metadata?.from && (
                            <div className="email-meta">
                                <p><strong>From:</strong> {data?.metadata?.from}</p>
                                <p><strong>To:</strong> {data?.metadata?.to}</p>
                                {data?.metadata?.cc && <p><strong>CC:</strong> {data?.metadata?.cc}</p>}
                            </div>
                        )}
                        {data.type === 'emails' ? (
                            <div
                                className='email-content-container'
                                dangerouslySetInnerHTML={{ __html: emailContent.html }}
                                style={{
                                    whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal",
                                    wordWrap: "break-word",
                                    maxWidth: "100%",
                                    overflowWrap: "break-word",
                                    overflowX: "hidden",
                                }}
                            />
                        ) : (
                            <div 
                                dangerouslySetInnerHTML={{ __html: highlightedContent }}
                                style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    maxWidth: "100%",
                                    overflowWrap: "break-word",
                                    overflowX: "hidden",
                                }}
                            />
                        )}
                    </div>
                    {communicationStatus && (
                        <div className={`activity-communication-status subtext ${communicationStatus.state}`}>
                            {communicationStatus.text}
                        </div>
                    )}
                    <div className="activity-reply-form" onClick={(e) => e.stopPropagation()}>
                        <div className='form-group activity'>
                            <label className='subtext'>Reply<span className='required'>*</span></label>
                            <MentionsTextarea
                                value={replyData.content}
                                onChange={(value) => setReplyData(prev => ({ ...prev, content: value }))}
                                onMentionsChange={(mentions) => setReplyData(prev => ({ ...prev, mentions }))}
                                users={users}
                                disabled={false}
                            />
                        </div>
                        <div className='form-group activity'>
                            <label className='subtext'>Attachments</label>
                            <MultiFile
                                value={replyData.attachments}
                                onChange={(val) => setReplyData({...replyData, attachments: val})}
                            />
                        </div>
                        <div className="reply-actions">
                            <button onClick={handleReply} className="action">Reply</button>
                            <button onClick={handleCancelReply} className="action alt">Cancel</button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {data?.subject != "" && <div className='activity-title'>
                        <span className="activity-title-text" dangerouslySetInnerHTML={{ __html: highlightedSubject }}></span><span className='item-type-line'></span>
                    </div>}
                    <div
                        className={`activity-feed-item-content ${activeItem === data.id ? 'expanded-activity' : ''} ${canExpand && activeItem !== data.id ? 'has-more' : ''}`}
                        ref={contentRef}
                        style={(data.type === 'emails' || data.type === '') ? { whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal", wordWrap: "break-word", maxWidth: "100%", overflowWrap: "break-word", overflowX: "hidden" } : {}}
                    >
                        {data?.type === 'emails' && data?.metadata?.from && (
                            <div className="email-meta subtext">
                                <p><strong>From:</strong> {data?.metadata?.from}</p>
                                <p><strong>To:</strong> {data?.metadata?.to}</p>
                                {data?.metadata?.cc && <p><strong>CC:</strong> {data?.metadata?.cc}</p>}
                                <br/>
                            </div>
                        )}
                        {data.type === 'emails' ? (
                            <div
                                className='email-content-container'
                                dangerouslySetInnerHTML={{ __html: emailContent.html }}
                                style={{
                                    whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal",
                                    wordWrap: "break-word",
                                    maxWidth: "100%",
                                    fontSize: '14px',
                                    overflowWrap: "break-word",
                                    overflowX: "hidden",
                                }}
                            />
                        ) : (
                            <div 
                                dangerouslySetInnerHTML={{ __html: highlightedContent }}
                                style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    maxWidth: "100%",
                                    fontSize: '14px',
                                    overflowWrap: "break-word",
                                    overflowX: "hidden",
                                }}
                            />
                        )}
                    </div>
                    {communicationStatus && (
                        <div className={`activity-communication-status subtext ${communicationStatus.state}`}>
                            {communicationStatus.text}
                        </div>
                    )}
                    {data.type === 'form' && (
                        <ActivityFormResponse data={data} userId={user_id} fetchFeed={fetchFeed} />
                    )}
                </>
            )}
            {!isEditing && !isReplying && (data.type === 'tasks' || data.type === 'notes') && (
                <div className='activity-feed-task-users'>
                    <div className='user-wrapper'>
                        <SearchSelect
                            value={''}
                            onChange={(val) => {
                                if (!canEditTaskAssignees || updatingAssignees) {
                                    return;
                                }
                                const assigneeId = Number(val);
                                if (assigneeId && !tempAssignees.includes(assigneeId)) {
                                    const nextAssignees = [...tempAssignees, assigneeId];
                                    setTempAssignees(nextAssignees);
                                    updateTaskAssignees(nextAssignees, { forceTaskType: data.type === 'notes' });
                                }
                            }}
                            options={users?.reduce((acc, user) => {
                                acc[user.id] = user.name;
                                return acc;
                            }, {})}
                            placeholder={canEditTaskAssignees ? "Assign user..." : "Assigned users"}
                            disabled={!canEditTaskAssignees || updatingAssignees}
                        />
                    </div>
                    {tempAssignees.length > 0 && (
                        <div className='activity-selected-users' style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            {tempAssignees.map((assigneeId) => {
                                const assigneeUser = users.find(u => Number(u.id) === Number(assigneeId))
                                    || (Array.isArray(data?.assignee_users)
                                        ? data.assignee_users.find(u => Number(u.id) === Number(assigneeId))
                                        : null);
                                if (!assigneeUser) return null;
                                return (
                                    <div key={assigneeId} className="assignees-list">
                                        {assigneeUser.name}
                                        {canEditTaskAssignees && (
                                            <div className='remove-assignee'>
                                                <X
                                                    size={14}
                                                    onClick={() => {
                                                        if (updatingAssignees) {
                                                            return;
                                                        }
                                                        const nextAssignees = tempAssignees.filter(id => id !== assigneeId);
                                                        setTempAssignees(nextAssignees);
                                                        updateTaskAssignees(nextAssignees, { forceTaskType: data.type === 'notes' });
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {Array.isArray(data.attachments) && data.attachments.length > 0 && <div className='files'>
                {data.attachments.slice(0, !hasOverflow ? 5 : data.attachments.length).map((file, index) => {
                    const filePath = file?.file_path || file?.key || '';
                    const normalizedPath = filePath.replace(/^cases\/\d+\//, '');
                    const withoutName = normalizedPath.replace(/^{{Name}}\//, '');
                    const folderFromPath = withoutName.includes('/')
                        ? withoutName.split('/').slice(0, -1).join('/')
                        : '';

                    const fallbackFolder = data.type !== "calls"
                        ? data.type === 'emails'
                            ? 'Emails'
                            : data.type === 'texts'
                                ? 'Client Communication'
                                : data.type === 'faxes'
                                    ? 'Faxes'
                                    : 'Notes'
                        : 'Call Log';

                    const targetFolder = folderFromPath || fallbackFolder;
                    const fileId = file?.id || file?.file_id;
                    const openFullView = () => {
                        if (!fileId) return;
                        window.open(`/file/${fileId}`, '_blank', 'noopener');
                    };

                    return (
                        <File
                            file={file}
                            key={index}
                            onClick={openFullView}
                            menuOptions={[
                                {
                                    label: 'Go to document location',
                                    onClick: () => navigate(`${window.location.pathname}?section=3&folder=${targetFolder}`)
                                },
                                {
                                    label: 'View full file',
                                    onClick: openFullView,
                                    disabled: !fileId
                                }
                            ]}
                        />
                    );
                })}
                {data.attachments.length > 5 && (
                    <div className='file small-text' onClick={(e) => {setHasOverflow(!hasOverflow); e.stopPropagation();}}>
                        {hasOverflow ? `show less` : `+${data.attachments.length - 5} more`}
                    </div>
                )}
            </div>}
            
            {/* Nested replies section */}
            {data.replies && data.replies.length > 0 && (
                <div className="activity-replies" onClick={(e) => e.stopPropagation()}>
                    {data.replies.map((reply) => {
                        const replyDepth = depth + 1;
                        const depthClass = replyDepth % 2 === 1 ? 'reply-depth-odd' : 'reply-depth-even';
                        return (
                            <div key={`reply-${reply.id}`} className={`activity-reply ${depthClass}`} onClick={(e) => e.stopPropagation()}>
                                <Activity
                                    data={reply}
                                    user_id={user_id}
                                    user={users.find((user) => String(user.id) === String(reply.task))?.name || "Unknown"}
                                    fetchFeed={fetchFeed}
                                    reminder={reminder}
                                    priority={priority}
                                    setActiveItem={setActiveItem}
                                    activeItem={activeItem}
                                    users={users}
                                    isReply={false} // Allow replies to be replyable
                                    activityKey={activityKey}
                                    searchQuery={searchQuery}
                                    searchSpecifier={searchSpecifier}
                                    depth={replyDepth}
                                    sections={sections}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
            
            <div className='activity-feed-item-type subtext'>
                {!isInheritedParentItem && !isEditing && !isReplying && !isReply && (
                    <div
                        className='activity-feed-item-reply-button'
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsReplying(true);
                        }}
                        role='button'
                        tabIndex={0}
                        aria-label='Reply to activity'
                        title='Reply'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsReplying(true);
                            }
                        }}
                    >
                        <ArrowUp size={14} />
                        <span>Reply</span>
                    </div>
                )}
                {data.section_id && sections?.find(section => section?.id === data?.section_id)?.name ? `From ${sections.find(section => section?.id === data?.section_id)?.name}` : null}
                {canExpand && (
                    <div className="expand-toggle" onClick={handleExpandClick}>
                        {activeItem === data.id ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                    </div>
                )}
                {iconMap[data.type === 'calls' ? 'Phone Calls' : 
                    data.type === 'eSignatures' ? 'eSignatures' : 
                    data.type.charAt(0).toUpperCase() + data.type.slice(1)]}
            </div>
            <div className='activity-feed-item-menu' ref={optionRef}>
                <div className='activity-menu-actions'>
                    {data?.deadline?.done && 
                        <div className='task-complete small-text' onClick={() => completeTask(false)} onMouseOver={() => setHoveringComplete(data?.id)} onMouseLeave={() => setHoveringComplete(null)}>
                            complete{hoveringComplete != data?.id ? <CheckCheck size={12} /> : <X size={12} />}
                        </div>
                    }
                    {data.pinned && (() => {
                        if (!isPinnedForUser && !isPinnedForCase) return null;

                        return (
                            <div className="activity-feed-item-pin">
                                {isPinnedForCase && (
                                    <div
                                        className='activity-pin case-pin'
                                        title='Pinned to Case Feed'
                                        onMouseOver={() => setHoveringPin("project")}
                                        onMouseLeave={() => setHoveringPin(null)}
                                    >
                                        {hoveringPin !== 'project'
                                            ? <Pin size={17} />
                                            : <PinOff size={17} onClick={() => pinNote("project", "remove")} />}
                                    </div>
                                )}
                                {isPinnedForUser && (
                                    <div
                                        className='activity-pin'
                                        title='Pinned to My Feed'
                                        onMouseOver={() => setHoveringPin("personal")}
                                        onMouseLeave={() => setHoveringPin(null)}
                                    >
                                        {hoveringPin !== 'personal'
                                            ? <Pin size={17} />
                                            : <PinOff size={17} onClick={() => pinNote(user_id, "remove")} />}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                    <Ellipsis className="activity-feed-item-options" size={20} onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        
                        if (!showOptions) {
                            // Calculate menu position relative to the ellipsis
                            const rect = e.currentTarget.getBoundingClientRect();
                            const windowHeight = window.innerHeight;
                            const windowWidth = window.innerWidth;
                            
                            // Menu dimensions (approximate)
                            const menuWidth = 150;
                            const menuHeight = 120;
                            
                            let top = rect.bottom + 5; // Position below the ellipsis
                            let left = rect.left - menuWidth + rect.width; // Align right edge
                            
                            // Adjust if menu would go below viewport
                            if (top + menuHeight > windowHeight) {
                                top = rect.top - menuHeight - 5; // Position above
                            }
                            
                            // Adjust if menu would go outside left edge
                            if (left < 0) {
                                left = rect.right - menuWidth;
                            }
                            
                            // Adjust if menu would go outside right edge
                            if (left + menuWidth > windowWidth) {
                                left = windowWidth - menuWidth - 10;
                            }
                            
                            setMenuPosition({ top, left });
                        }
                        
                        setShowOptions((prev) => !prev);
                    }}/>
                </div>
                {showOptions && (
                    <div 
                        className="activity-feed-item-options-menu"
                        style={{
                            top: `${menuPosition.top}px`,
                            left: `${menuPosition.left}px`,
                            zIndex: 100001
                        }}
                    >
                        {canEdit && !isEditing && <div className="activity-feed-item-option" onClick={() => { setIsEditing(true); setShowOptions(false); }}>Edit</div>}
                        {!isInheritedParentItem && <div className="activity-feed-item-option" onClick={() => { setIsReplying(true); setShowOptions(false); }}>Reply</div>}
                        {!isInheritedParentItem && data?.type === 'emails' && (
                            <div className="activity-feed-item-option" onClick={handleGenerateEmailPdf}>
                                {generatingEmailPdf ? 'Generating PDF...' : 'Generate PDF'}
                            </div>
                        )}
                        {!isInheritedParentItem && <div 
                            className="activity-feed-item-option" 
                            onClick={() => !isPinnedForUser ? pinNote(user_id) : pinNote(user_id, 'remove')}
                        >
                            {isPinnedForUser ? 'Unpin from my feed' : 'Pin to My Feed'}
                        </div>}
                        {!isInheritedParentItem && <div 
                            className="activity-feed-item-option" 
                            onClick={() => !isPinnedForCase ? pinNote('project') : pinNote('project', 'remove')}
                        >
                            {isPinnedForCase ? 'Unpin from Case' : 'Pin to Case'}
                        </div>}
                        {!isInheritedParentItem && <div className="activity-feed-item-option" onClick={() => {
                            setShowMoveModal(true);
                            setShowOptions(false);
                        }}>
                            Move to...
                        </div>}
                        {!isInheritedParentItem && <div className="activity-feed-item-option" onClick={() => {
                            setShowCopyModal(true);
                            setShowOptions(false);
                        }}>
                            Copy to...
                        </div>}
                        {isInheritedParentItem && data?.can_exclude_from_child && (
                            <div className="activity-feed-item-option" onClick={handleExcludeInheritedFromChild}>
                                Hide from this child
                            </div>
                        )}
                        {canSyncToChildCases && !isInheritedParentItem && (
                            <div className="activity-feed-item-option" onClick={() => {
                                console.log('[ACTIVITY_FEED] Sync item requested', data?.id);
                                setShowOptions(false);
                            }}>
                                Sync
                            </div>
                        )}
                        {canDelete && (
                            <div className="activity-feed-item-option" onClick={handleDelete}>
                                Delete
                            </div>
                        )}
                        {data?.deadline?.assignees?.length > 0 && <div className="activity-feed-item-option" onClick={() => completeTask(data?.deadline?.done === null)}>{data?.deadline?.done ? 'Mark task incomplete' : 'Mark task complete'}</div>}
                    </div>
                )}

            {/* Move Activity Modal */}
            {showMoveModal && (
                <Modal
                    title="Move Activity to Case"
                    instructions="Select a case to move this activity to."
                    onClose={() => setShowMoveModal(false)}
                    overflowy={true}
                    footer={
                        <div className='modal-footer-actions'>
                            <button onClick={() => setShowMoveModal(false)} className="action alt">Cancel</button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper" style={{ overflowY: 'visible' }}>
                        <div className="form-group full">
                            <SearchSelect
                                value=""
                                onChange={(val) => {
                                    if (val && window.confirm('Are you sure you want to move this activity to the selected case?')) {
                                        handleMoveActivity(parseInt(val));
                                    }
                                }}
                                loadOptions={loadCaseOptions}
                                loadOptionsMinChars={2}
                                placeholder="Search for a case..."
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* Copy Activity Modal */}
            {showCopyModal && (
                <Modal
                    title="Copy Activity to Cases"
                    instructions="Search and select cases to copy this activity to."
                    onClose={() => {
                        setShowCopyModal(false);
                        setSelectedCases([]);
                    }}
                    overflowy={true}
                    footer={
                        <div className='modal-footer-actions'>
                            <button 
                                onClick={handleCopyActivity} 
                                className="action"
                                disabled={selectedCases.length === 0}
                            >
                                Copy to {selectedCases.length} case(s)
                            </button>
                            <button onClick={() => {
                                setShowCopyModal(false);
                                setSelectedCases([]);
                            }} className="action alt">Cancel</button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper" style={{ maxHeight: '80vh', overflowY: 'visible' }}>
                        <div className="form-group full">
                            <SearchSelect
                                value=""
                                onChange={(option) => {
                                    if (option?.value) {
                                        const caseId = parseInt(option.value, 10);
                                        if (!selectedCases.includes(caseId)) {
                                            setSelectedCases([...selectedCases, caseId]);
                                        }
                                        setAvailableCases((prev) => prev.some(c => parseInt(c.value, 10) === caseId) ? prev : [...prev, option]);
                                    }
                                }}
                                options={availableCases.filter(c => !selectedCases.includes(parseInt(c.value, 10)))}
                                loadOptions={loadCaseOptions}
                                loadOptionsMinChars={2}
                                returnOptionObject={true}
                                placeholder="Search and select a case..."
                            />
                        </div>

                        {selectedCases.length > 0 && (
                            <div className="form-group full">
                                <label className='subtext'>Selected Cases ({selectedCases.length}):</label>
                                <div className='selected-cases'>
                                    {selectedCases.map(caseId => {
                                        const caseItem = availableCases.find(c => parseInt(c.value) === caseId);
                                        return caseItem ? (
                                            <div key={caseId} className='selected-case'>
                                                <span>{caseItem.label}</span>
                                                <X
                                                    onClick={() => setSelectedCases(selectedCases.filter(id => id !== caseId))}
                                                    className='remove-case-btn'
                                                    title="Remove case"
                                                />
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
            </div>
        </div>
    )
}

export const ActivityFeed = ({ case_id, user_id, lead_id = null, activityKey = null, sections = [], settlementActivityDraft = null, disengagementActivityDraft = null, guidedActivityDraft = null, onSettlementDraftConsumed = () => {}, onDisengagementDraftConsumed = () => {}, onGuidedDraftConsumed = () => {}, onSettlementDraftSubmitted = () => {}, onDisengagementDraftSubmitted = () => {}, onSettlementDraftCancelled = () => {}, onDisengagementDraftCancelled = () => {}, childCaseCount = 0 }) => {
        const handleSyncFeed = () => {
            console.log('[ACTIVITY_FEED] Sync feed requested');
        };
    const location = useLocation();
    const [feed, setFeed] = useState([]);
    const [activeFeed, setActiveFeed] = useState("All");
    const [expanded, setExpanded] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchSpecifier, setSearchSpecifier] = useState("all");
    const [addActivity, setAddActivity] = useState(false);
    const [users, setUsers] = useState([]);
    const [mobile, setMobile] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [composerDraft, setComposerDraft] = useState(null);
    const [createContact, setCreateContact] = useState(false);
    const [activityCreatedContact, setActivityCreatedContact] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [itemCount, setItemCount] = useState(0);
    const [loadingFeed, setLoadingFeed] = useState(false);
    const [loggedInUserId, setLoggedInUserId] = useState(user_id || null);
    const navigate = useNavigate();
    const perPage = 100;
    const hasLinkedChildCases = Number(childCaseCount) > 0;
    
    // Scroll buttons state
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);
    const feedTypesRef = useRef(null);
    const activityFetchRef = useRef(new Set());
    const loadedPagesRef = useRef(new Set());
    const liveCursorRef = useRef('');
    const feedFetchVersionRef = useRef(0);
    const feedDataVersionRef = useRef(0);
    const liveMarkerTimersRef = useRef(new Map());
    const feedRef = useRef([]);
    const pageRef = useRef(1);
    const foregroundFeedRequestRef = useRef(null);

    useEffect(() => {
        feedRef.current = feed;
    }, [feed]);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const effectiveUserId = loggedInUserId ?? user_id;

    const reminder = feed.filter((data) => (
        data.deadline?.due &&
        data.deadline?.done === null &&
        Array.isArray(data.deadline.assignees) &&
        data.deadline.assignees.map(Number).includes(Number(effectiveUserId)) &&
        new Date(data.deadline.due) > new Date().setDate(new Date().getDate() + 7)
    ));

    const priority = feed.filter((data) => (
        data.deadline?.due &&
        data.deadline?.done === null &&
        Array.isArray(data.deadline.assignees) &&
        data.deadline.assignees.map(Number).includes(Number(effectiveUserId)) &&
        new Date(data.deadline.due) <= new Date().setDate(new Date().getDate() + 7)
    ));

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setMobile(true);
            } else {
                setMobile(false);
            }
        };
        handleResize();
    }, []);

    useOptimizedResize(() => {
        setMobile(window.innerWidth <= 768);
    });

    useEffect(() => {
        handleActiveItem();
    }, [activityKey, feed]); // Also depend on feed so it triggers when feed loads

    useEffect(() => {
        if (!activityKey) return;
        const keyStr = String(activityKey);
        if (feed.some(item => String(item?.id) === keyStr)) return;
        if (activityFetchRef.current.has(keyStr)) return;

        activityFetchRef.current.add(keyStr);

        const fetchActivityById = async () => {
            try {
                const response = await fetch(`https://api.casedb.co/activity_feed.php?id=${encodeURIComponent(keyStr)}`, { credentials: 'include' });
                const data = await response.json();
                if (data?.logged_in_user_id) {
                    setLoggedInUserId(Number(data.logged_in_user_id));
                }
                const activityItem = Array.isArray(data.feed) ? data.feed[0] : null;
                if (data.success && activityItem) {
                    setFeed(prev => {
                        const exists = prev.some(item => String(item?.id) === keyStr);
                        return exists ? prev : [activityItem, ...prev];
                    });
                }
            } catch (error) {
                console.error('Error fetching activity by id:', error);
            }
        };

        fetchActivityById();
    }, [activityKey, feed]);

    // Only scroll when activityKey changes (URL navigation), not when user clicks
    useEffect(() => {
        if (!activityKey) return;

        const timeoutId = setTimeout(() => {
            const element = document.getElementById(`activity-${activityKey}`);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                // Set activeItem to keep it highlighted even after key is cleared
                setActiveItem(parseInt(activityKey));

                // Clear key after the scroll/highlight has a chance to run
                const params = new URLSearchParams(location.search);
                if (params.has('key')) {
                    params.delete('key');
                    const nextSearch = params.toString();
                    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash || ''}`, { replace: true });
                }
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [activityKey, feed, location.pathname, location.search, location.hash, navigate]);

    const handleActiveItem = () => {
        if (activityKey) {
            // Set this item as active to expand it (scrolling is handled by the useEffect above)
            setActiveItem(parseInt(activityKey));
        }
    }

    const today = new Date().toDateString();

    const matchesCurrentFeed = useCallback((item, itemById = new Map()) => {
        const parentItem = item?.reply ? itemById.get(String(item.reply)) : null;
        const normalizedActiveFeed = String(activeFeed || 'All').toLowerCase();
        const normalizedType = String(item?.type || '').toLowerCase();
        const matchesType = normalizedActiveFeed === 'all'
            || normalizedType === normalizedActiveFeed
            || (normalizedActiveFeed === 'reminders' && normalizedType === 'tasks')
            || (normalizedActiveFeed === 'form' && String(parentItem?.type || '').toLowerCase() === 'form');
        if (!matchesType) return false;

        const query = searchQuery.trim().toLowerCase();
        if (!query) return true;
        const subject = String(item?.subject || '');
        const content = String(item?.content || '');
        const searchText = searchSpecifier === 'subject'
            ? subject
            : searchSpecifier === 'content'
                ? content
                : `${subject} ${content}`;
        return searchText.toLowerCase().includes(query);
    }, [activeFeed, searchQuery, searchSpecifier]);

    const scheduleNewMarkerExpiry = useCallback((ids = []) => {
        ids.forEach((id) => {
            const normalizedId = String(id);
            const existingTimer = liveMarkerTimersRef.current.get(normalizedId);
            if (existingTimer) window.clearTimeout(existingTimer);
            const timerId = window.setTimeout(() => {
                liveMarkerTimersRef.current.delete(normalizedId);
                const clearMarker = (items) => items.map((item) => (
                    String(item?.id) === normalizedId
                        ? { ...item, __liveNew: false, __liveNewAt: undefined }
                        : item
                ));
                feedRef.current = clearMarker(feedRef.current);
                setFeed((items) => clearMarker(items));
            }, ACTIVITY_FEED_NEW_MARKER_MS);
            liveMarkerTimersRef.current.set(normalizedId, timerId);
        });
    }, []);

    useEffect(() => () => {
        liveMarkerTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        liveMarkerTimersRef.current.clear();
    }, []);

    const requestFeedPage = useCallback(async (nextPage, { signal } = {}) => {
        const entityParam = case_id ? `case_id=${case_id}` : `lead_id=${lead_id}`;
        const feedTypeParam = activeFeed ? encodeURIComponent(activeFeed) : 'All';
        const searchParam = searchQuery && searchQuery.trim() !== '' ? `&search_query=${encodeURIComponent(searchQuery)}` : '';
        const searchSpecParam = searchSpecifier ? `&search_specifier=${encodeURIComponent(searchSpecifier)}` : '';
        const response = await fetch(`${caseDbApiEndpoint('activity_feed.php')}?${entityParam}&feed_type=${feedTypeParam}${searchParam}${searchSpecParam}&page=${nextPage}&per_page=${perPage}&time=${Date.now()}`, {
            credentials: 'include',
            signal,
        });
        const data = await response.json();
        if (!response.ok || !data?.success) {
            throw new Error(data?.message || 'Unable to load the activity feed.');
        }
        return data;
    }, [activeFeed, case_id, lead_id, searchQuery, searchSpecifier]);

    const fetchFeed = useCallback(async ({ page: nextPage = 1, append = false, background = false } = {}) => {
        if (append && loadedPagesRef.current.has(nextPage)) return;
        if (append && foregroundFeedRequestRef.current) return;
        const requestVersion = ++feedFetchVersionRef.current;
        const foregroundRequest = background ? null : Symbol('activity-feed-foreground-load');
        if (foregroundRequest) {
            foregroundFeedRequestRef.current = foregroundRequest;
            setLoadingFeed(true);
        }
        try {
            const data = await requestFeedPage(nextPage);
            if (requestVersion !== feedFetchVersionRef.current) return;
            if (data?.logged_in_user_id) {
                setLoggedInUserId(Number(data.logged_in_user_id));
            }
            if (!append && data?.live_cursor) {
                liveCursorRef.current = String(data.live_cursor);
            }
            const nextFeed = Array.isArray(data.feed_updates) ? data.feed_updates : [];
            // Commit the ref and state together. A queued React state updater
            // can otherwise lag behind a live response and let one request
            // overwrite rows committed by the other.
            const reconciledFeed = mergeActivityFeedPage(feedRef.current, nextFeed, { append });
            feedDataVersionRef.current += 1;
            feedRef.current = reconciledFeed;
            setFeed(reconciledFeed);
            pageRef.current = nextPage;
            setPage(nextPage);
            setHasMore(!!data.pagination?.has_more);
            setItemCount(data.pagination?.total || 0);
            if (!append) loadedPagesRef.current = new Set();
            loadedPagesRef.current.add(nextPage);
            return true;
        } catch (error) {
            console.error("Error fetching activity feed:", error);
            return false;
        } finally {
            if (foregroundRequest && foregroundFeedRequestRef.current === foregroundRequest) {
                foregroundFeedRequestRef.current = null;
                setLoadingFeed(false);
            }
        }
    }, [requestFeedPage]);

    const resyncFeedInBackground = useCallback(async ({ signal } = {}) => {
        const requestVersion = ++feedFetchVersionRef.current;
        const loadedPageNumbers = Array.from(loadedPagesRef.current)
            .map(Number)
            .filter((value) => Number.isInteger(value) && value > 0);
        const targetPage = Math.max(1, pageRef.current, ...loadedPageNumbers);
        const snapshotItems = [];
        let firstData = null;
        let lastData = null;
        let lastPage = 0;

        try {
            for (let nextPage = 1; nextPage <= targetPage; nextPage += 1) {
                if (signal?.aborted) return false;
                const data = await requestFeedPage(nextPage, { signal });
                if (signal?.aborted || requestVersion !== feedFetchVersionRef.current) return false;
                if (!firstData) firstData = data;
                lastData = data;
                lastPage = nextPage;
                snapshotItems.push(...(Array.isArray(data.feed_updates) ? data.feed_updates : []));
                if (!data.pagination?.has_more) break;
            }

            if (signal?.aborted || !firstData || requestVersion !== feedFetchVersionRef.current) return false;

            const dedupedSnapshot = mergeActivityFeedPage([], snapshotItems, { append: true });
            const reconciledFeed = mergeActivityFeedPage(feedRef.current, dedupedSnapshot);
            feedDataVersionRef.current += 1;
            if (reconciledFeed !== feedRef.current) {
                feedRef.current = reconciledFeed;
                setFeed(reconciledFeed);
            }
            if (firstData.logged_in_user_id) {
                setLoggedInUserId(Number(firstData.logged_in_user_id));
            }
            pageRef.current = Math.max(1, lastPage);
            setPage(Math.max(1, lastPage));
            setHasMore(!!lastData?.pagination?.has_more);
            setItemCount(firstData.pagination?.total || 0);
            loadedPagesRef.current = new Set(
                Array.from({ length: Math.max(1, lastPage) }, (_, index) => index + 1),
            );

            // Advance only after the complete replacement succeeds. Keeping
            // the stale cursor on an error lets the next poll request another
            // bounded resync instead of leaving live polling permanently idle.
            if (firstData.live_cursor) {
                liveCursorRef.current = String(firstData.live_cursor);
            }
            return true;
        } catch (error) {
            if (error?.name !== 'AbortError') {
                console.warn('Unable to resync the activity feed in the background:', error);
            }
            return false;
        }
    }, [requestFeedPage]);

    useEffect(() => {
        if (!case_id && !lead_id) return undefined;

        let stopped = false;
        let timerId = null;
        const pollGate = createActivityFeedLivePollGate();

        const schedule = (delay = ACTIVITY_FEED_LIVE_POLL_MS) => {
            if (stopped || document.hidden) return;
            window.clearTimeout(timerId);
            timerId = window.setTimeout(() => {
                timerId = null;
                poll();
            }, delay);
        };

        const poll = async () => {
            const since = liveCursorRef.current;
            if (stopped || document.hidden || !since) {
                schedule();
                return;
            }

            const requestVersion = feedFetchVersionRef.current;
            const dataVersion = feedDataVersionRef.current;
            const entityParam = case_id ? ['case_id', case_id] : ['lead_id', lead_id];
            const changes = [];
            const scopeChanges = [];
            let snapshotCursor = '';
            let nextPage = 1;
            let hasMoreChanges = false;
            let hasMoreScopeChanges = false;
            const liveRun = {
                id: Symbol('activity-feed-live-poll'),
                controller: new AbortController(),
            };
            if (!pollGate.begin(liveRun)) return;

            try {
                do {
                    const params = new URLSearchParams({
                        [entityParam[0]]: String(entityParam[1]),
                        live: '1',
                        live_since: since,
                        page: String(nextPage),
                        per_page: '250',
                        time: String(Date.now()),
                    });
                    if (snapshotCursor) params.set('live_until', snapshotCursor);

                    const { ok, data } = await requestActivityFeedLivePage({
                        url: `${caseDbApiEndpoint('activity_feed.php')}?${params.toString()}`,
                        signal: liveRun.controller.signal,
                    });
                    if (!ok || !data?.success) {
                        if (activityFeedLiveResponseRequiresResync({ ok, data })) {
                            // The server bounds delta windows so a suspended
                            // tab cannot request an arbitrarily large history.
                            // Reconcile every loaded page without blanking the
                            // feed or disturbing its current scroll position.
                            await resyncFeedInBackground({ signal: liveRun.controller.signal });
                            return;
                        }
                        throw new Error(data?.message || 'Unable to refresh the activity feed.');
                    }

                    if (!snapshotCursor) snapshotCursor = String(data.live_cursor || '');
                    changes.push(...(Array.isArray(data.feed_updates) ? data.feed_updates : []));
                    scopeChanges.push(...(Array.isArray(data.scope_changes) ? data.scope_changes : []));
                    hasMoreChanges = !!data.pagination?.has_more;
                    hasMoreScopeChanges = !!data.scope_pagination?.has_more;
                    nextPage += 1;
                } while (
                    (hasMoreChanges || hasMoreScopeChanges)
                    && nextPage <= 4
                    && !stopped
                    && !liveRun.controller.signal.aborted
                );

                if (
                    stopped
                    || !canCommitActivityLiveSnapshot(
                        requestVersion,
                        feedFetchVersionRef.current,
                        dataVersion,
                        feedDataVersionRef.current,
                    )
                ) return;

                if (hasMoreChanges || hasMoreScopeChanges) {
                    // More than 1,000 changes inside one 15-second window is
                    // exceptional. Fall back to the normal bounded first page
                    // instead of applying an incomplete delta snapshot.
                    await resyncFeedInBackground({ signal: liveRun.controller.signal });
                    return;
                }

                const scopedChanges = applyActivityScopeChanges(changes, scopeChanges);
                const result = mergeActivityFeedChanges(feedRef.current, scopedChanges, {
                    matches: matchesCurrentFeed,
                    now: Date.now(),
                });
                if (result.items !== feedRef.current) {
                    feedRef.current = result.items;
                    setFeed(result.items);
                }
                if (result.countDelta !== 0) {
                    setItemCount((count) => Math.max(0, Number(count || 0) + result.countDelta));
                }
                if (result.newIds.length > 0) scheduleNewMarkerExpiry(result.newIds);
                if (snapshotCursor) liveCursorRef.current = snapshotCursor;
            } catch (error) {
                if (error?.name !== 'AbortError') {
                    console.warn('Unable to refresh the activity feed:', error);
                }
            } finally {
                const completion = pollGate.finish(liveRun);
                if (!completion.released || stopped || document.hidden) return;
                schedule(completion.rerunRequested ? 0 : ACTIVITY_FEED_LIVE_POLL_MS);
            }
        };

        const handleVisibilityChange = () => {
            window.clearTimeout(timerId);
            timerId = null;
            if (document.hidden) {
                pollGate.cancelRerun();
                pollGate.abortActiveRun();
                return;
            }
            if (pollGate.hasActiveRun()) {
                pollGate.requestRerun();
                return;
            }
            poll();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        schedule();

        return () => {
            stopped = true;
            window.clearTimeout(timerId);
            pollGate.cancelRerun();
            pollGate.abortActiveRun();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [case_id, lead_id, activeFeed, searchQuery, searchSpecifier, matchesCurrentFeed, resyncFeedInBackground, scheduleNewMarkerExpiry]);
    
    const fetchUsers = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/user.php?users=true&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (response.ok) {
                const processedUsers = data.users.map(user => ({
                    ...user,
                    user: user.user || user.name.toLowerCase().replace(/\s+/g, '_')
                }));
                setUsers(processedUsers);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    }

    useEffect(() => {
        if (!settlementActivityDraft?.id || users.length === 0) {
            return;
        }

        const settlementUsers = users.filter(user => (
            Number(user?.settlement) === 1
            && !['cbrown', 'rdaly', 'jblack', 'adao'].includes(String(user?.user || '').trim().toLowerCase())
        ));
        const mentions = settlementUsers
            .map(user => Number(user?.id))
            .filter(id => !Number.isNaN(id));
        const content = settlementUsers
            .map(user => user?.user ? `@${user.user}` : '')
            .filter(Boolean)
            .join(' ');

        setComposerDraft({
            id: settlementActivityDraft.id,
            type: 'notes',
            subject: 'Settlement phase initiated',
            content: content ? `${content} ` : '',
            mentions,
            lockedType: 0,
            lockSubject: true,
            settlementTriggerRoute: true,
            completeSettlementPhaseAfterNote: true,
            settlementPhaseId: settlementActivityDraft.phaseId || 19
        });
        setAddActivity(true);
        onSettlementDraftConsumed?.();
    }, [settlementActivityDraft, users, onSettlementDraftConsumed]);

    useEffect(() => {
        if (!disengagementActivityDraft?.id || users.length === 0) {
            return;
        }

        const ksatriano = users.find(user => String(user?.user || '').toLowerCase() === 'ksatriano');
        const mentions = ksatriano?.id ? [Number(ksatriano.id)] : [];
        const content = String(disengagementActivityDraft.content || '').trim() || '@ksatriano Pending disengagement.';

        setComposerDraft({
            id: disengagementActivityDraft.id,
            type: 'notes',
            subject: disengagementActivityDraft.subject || 'Pending disengagement',
            content,
            mentions,
            tags: ['disengagement'],
            lockedType: 0,
            lockSubject: true,
            disengagementTriggerRoute: true,
            completeDisengagementPhaseAfterNote: true,
            disengagementPhaseId: disengagementActivityDraft.phaseId || 3,
            disengagementDefaultContent: content,
            disengagementFieldValues: disengagementActivityDraft.fieldValues || []
        });
        setAddActivity(true);
        onDisengagementDraftConsumed?.();
    }, [disengagementActivityDraft, users, onDisengagementDraftConsumed]);

    useEffect(() => {
        if (!guidedActivityDraft?.id) {
            return;
        }

        setComposerDraft({
            id: guidedActivityDraft.id,
            type: guidedActivityDraft.type || 'notes',
            subject: guidedActivityDraft.subject || '',
            content: guidedActivityDraft.content || '',
            mentions: Array.isArray(guidedActivityDraft.mentions) ? guidedActivityDraft.mentions : [],
            tags: Array.isArray(guidedActivityDraft.tags) ? guidedActivityDraft.tags : [],
            lockedType: guidedActivityDraft.lockedType ?? (guidedActivityDraft.type === 'tasks' ? 1 : 0),
            lockSubject: !!guidedActivityDraft.lockSubject,
            case_id: guidedActivityDraft.case_id || case_id,
            lead_id: guidedActivityDraft.lead_id || lead_id,
            post: false,
        });
        setAddActivity(true);
        onGuidedDraftConsumed?.();
    }, [guidedActivityDraft, case_id, lead_id, onGuidedDraftConsumed]);

    const fetchContacts = async () => {
        try {
            // Only fetch contacts if we have case_id (not for leads)
            if (case_id) {
                const response = await fetch(`https://api.casedb.co/contacts.php?case_id=${case_id}&time=${new Date().getTime()}`, { credentials: 'include' });
                const data = await response.json();
                if (data.success) {
                    setContacts(data.contacts || []);
                } else {
                    console.error("Error fetching contacts:", data.message);
                }
            }
        } catch (error) {
            console.error("Error fetching contacts:", error);
        }
    }

    const handleActivityContactCreated = (contact) => {
        if (!contact?.id) {
            return;
        }

        setActivityCreatedContact(contact);
        setContacts((prev) => {
            const existing = prev.filter((item) => Number(item?.id) !== Number(contact.id));
            return [contact, ...existing];
        });
        fetchContacts();
    };

    const exportToPDF = async () => {
        try {
            const entityParam = case_id ? `case_id=${case_id}` : `lead_id=${lead_id}`;
            const url = `https://api.casedb.co/activity_feed.php?${entityParam}&export_pdf=true&feed_type=${encodeURIComponent(activeFeed)}&time=${new Date().getTime()}`;
            
            // Create a temporary link to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `Activity_Feed_${activeFeed}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error exporting to PDF:", error);
            alert("Failed to export PDF. Please try again.");
        }
    }

    useEffect(() => {
        loadedPagesRef.current = new Set();
        fetchFeed({ page: 1, append: false });
    }, [fetchFeed]);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchContacts();
    }, [case_id]);

    // Helper function to highlight matching text
    const highlightMatches = (text, query) => {
        if (!text || !query || query.trim() === '') return text;
        
        const result = fuzzysort.single(query, text);
        if (!result) return text;
        
        return fuzzysort.highlight(result, '<span class="feed-match">', '</span>') || text;
    };

    const maxVisibleCount = page * perPage;
    const cappedFeed = feed.length > maxVisibleCount ? feed.slice(0, maxVisibleCount) : feed;
    const cappedFeedById = new Map(cappedFeed.map(item => [Number(item?.id), item]));

    const filteredFeed = cappedFeed.filter((item) => {
        const parentItem = item?.reply ? cappedFeedById.get(Number(item.reply)) : null;
        const matchesType = (
            item?.type?.toLowerCase() === String(activeFeed)?.toLowerCase() ||
            activeFeed === "All" ||
            (item.type === "tasks" && activeFeed === "Reminders") ||
            (activeFeed === "form" && parentItem?.type === "form")
        );
    
        const matchesSearch = searchQuery.trim() === '' || (() => {
            let searchText = '';
            
            if (searchSpecifier === 'all') {
                searchText = `${item.subject ?? ''} ${item.content ?? ''}`;
            } else if (searchSpecifier === 'subject') {
                searchText = item.subject ?? '';
            } else if (searchSpecifier === 'content') {
                searchText = item.content ?? '';
            }
            
            // Use case-insensitive substring matching instead of fuzzy matching
            return searchText.toLowerCase().includes(searchQuery.toLowerCase());
        })();
    
        return matchesType && matchesSearch && item !== "No activity found" && item.deleted !== 1;
    });

    // Organize replies - support nested replies
    const organizeReplies = (feedItems) => {
        const itemsMap = new Map();
        const topLevelItems = [];
        
        // First pass: create a map of all items and initialize replies array
        feedItems.forEach(item => {
            itemsMap.set(item.id, { ...item, replies: [] });
        });
        
        // Second pass: organize into hierarchy
        feedItems.forEach(item => {
            if (!item.reply) {
                // Top-level item
                topLevelItems.push(itemsMap.get(item.id));
                return;
            }

            // This is a reply - attach to parent if present
            const parent = itemsMap.get(item.reply);
            if (parent) {
                parent.replies.push(itemsMap.get(item.id));
            }
        });
        
        // Sort function for replies by ID (ascending order)
        const sortRepliesById = (items) => {
            items.forEach(item => {
                if (item.replies && item.replies.length > 0) {
                    item.replies.sort((a, b) => a.id - b.id);
                    sortRepliesById(item.replies); // Recursively sort nested replies
                }
            });
        };
        
        // Sort all replies by ID
        sortRepliesById(topLevelItems);
        
        // Sort top level items by creation date (newest first), fallback to id
        const toTimestamp = (item) => {
            const raw = item?.creation_date;
            if (!raw || typeof raw !== 'string') return null;
            const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
            const ts = Date.parse(normalized + (normalized.endsWith('Z') ? '' : 'Z'));
            return Number.isFinite(ts) ? ts : null;
        };

        return topLevelItems.sort((a, b) => {
            const aTime = toTimestamp(a);
            const bTime = toTimestamp(b);
            if (aTime !== null && bTime !== null && aTime !== bTime) {
                return bTime - aTime;
            }
            const aId = Number(a?.id) || 0;
            const bId = Number(b?.id) || 0;
            return bId - aId;
        });
    };

    const organizedFeed = organizeReplies(filteredFeed);

    const pinnedFeed = organizedFeed.filter((item) => {
        if (!item.pinned) return false;
        const pinData = typeof item.pinned === 'string' ? JSON.parse(item.pinned) : item.pinned;
        const isPinnedForUser = Array.isArray(pinData?.users) && pinData.users.includes(Number(user_id));
        const isPinnedForCase = pinData?.case === 1;
        return isPinnedForUser || isPinnedForCase;
    });    

    const pinnedIds = new Set(pinnedFeed.map(item => item.id));

    const myTasks = organizedFeed.filter((item) => 
        item.task === effectiveUserId && item.deadline?.done === null
    );

    const remainingFeed = organizedFeed.filter((item) => (
        !pinnedIds.has(item.id) &&
        !myTasks.some(task => task.id === item.id)
    ));

    const feedSections = [];

    if (pinnedFeed.length > 0) {
        feedSections.push(
            <React.Fragment key="pinned-section">
                <div className="activity-feed-header subtext">
                    <span>Pinned</span>
                    <div className='divider horizontal'></div>
                    <span>{pinnedFeed.length} {pinnedFeed.length > 1 ? "items" : "item"}</span>
                </div>
                {pinnedFeed.map((item, index) => (
                    <Activity
                        key={`pinned-${item?.id ?? index}`}
                        data={item}
                        caseId={case_id}
                        user_id={effectiveUserId}
                        user={users.find((user) => String(user.id) === String(item.task))?.name || "Unknown"}
                        fetchFeed={fetchFeed}
                        reminder={reminder}
                        priority={priority}
                        setActiveItem={setActiveItem}
                        activeItem={activeItem}
                        users={users}
                        activityKey={activityKey}
                        searchQuery={searchQuery}
                        searchSpecifier={searchSpecifier}
                        sections={sections}
                        canSyncToChildCases={hasLinkedChildCases}
                    />
                ))}
            </React.Fragment>
        );
    }

    if (myTasks.length > 0) {
        feedSections.push(
            <React.Fragment key="tasks-section">
                <div className="activity-feed-header subtext">
                    <span>My Assigned Tasks</span>
                    <div className='divider horizontal'></div>
                    <span>{myTasks.length} {myTasks.length > 1 ? "items" : "item"}</span>
                </div>
                {myTasks.map((item, index) => (
                    <Activity
                        key={`task-${item?.id ?? index}`}
                        data={item}
                        caseId={case_id}
                        user_id={effectiveUserId}
                        user={users.find((user) => String(user.id) === String(item.task))?.name || "Unknown"}
                        fetchFeed={fetchFeed}
                        reminder={reminder}
                        priority={priority}
                        setActiveItem={setActiveItem}
                        activeItem={activeItem}
                        users={users}
                        activityKey={activityKey}
                        searchQuery={searchQuery}
                        searchSpecifier={searchSpecifier}
                        sections={sections}
                        canSyncToChildCases={hasLinkedChildCases}
                    />
                ))}
            </React.Fragment>
        );
    }

    if (remainingFeed.length > 0) {
        feedSections.push(
            <React.Fragment key="activity-section">
                <div className="activity-feed-header subtext">
                    <span>Activity</span>
                    <div className='divider horizontal'></div>
                    <span>
                        {remainingFeed.length} {remainingFeed.length > 1 ? "items" : "item"}
                        {itemCount ? ` of ${itemCount}` : ''}
                    </span>
                </div>
                {remainingFeed.map((item, index) => (
                    <Activity
                        key={`activity-${item?.id ?? index}`}
                        data={item}
                        caseId={case_id}
                        user_id={effectiveUserId}
                        user={users.find((user) => String(user.id) === String(item.task))?.name || "Unknown"}
                        fetchFeed={fetchFeed}
                        reminder={reminder}
                        priority={priority}
                        setActiveItem={setActiveItem}
                        activeItem={activeItem}
                        users={users}
                        activityKey={activityKey}
                        searchQuery={searchQuery}
                        searchSpecifier={searchSpecifier}
                        sections={sections}
                        canSyncToChildCases={hasLinkedChildCases}
                    />
                ))}
            </React.Fragment>
        );
    }

    if (hasMore) {
        feedSections.push(
            <div key="load-more" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <button
                    className='action alt'
                    onClick={() => fetchFeed({ page: page + 1, append: true })}
                    disabled={loadingFeed}
                >
                    {loadingFeed ? 'Loading...' : 'Load more'}
                </button>
            </div>
        );
    }


    // Check if feed-types container has overflow
    const checkScrollButtons = useCallback(() => {
        if (!feedTypesRef.current) return;

        const container = feedTypesRef.current;
        const items = Array.from(container.children);

        if (items.length === 0) {
            setShowLeftScroll(false);
            setShowRightScroll(false);
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const firstItemRect = items[0].getBoundingClientRect();
        const lastItemRect = items[items.length - 1].getBoundingClientRect();
        const edgeTolerance = 4;

        const leftHidden = firstItemRect.left < containerRect.left - edgeTolerance;
        const rightHidden = lastItemRect.right > containerRect.right + edgeTolerance;

        setShowLeftScroll(leftHidden);
        setShowRightScroll(rightHidden);
    }, []);

    // Handle scroll button clicks
    const handleScrollLeft = () => {
        if (feedTypesRef.current) {
            feedTypesRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const handleScrollRight = () => {
        if (feedTypesRef.current) {
            feedTypesRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    // Check scroll buttons on mount and when window resizes
    useEffect(() => {
        checkScrollButtons();
        window.addEventListener('resize', checkScrollButtons);
        return () => window.removeEventListener('resize', checkScrollButtons);
    }, [checkScrollButtons]);

    // Also check when mobile state changes or feed types might change
    useEffect(() => {
        const frameId = window.requestAnimationFrame(checkScrollButtons);
        return () => window.cancelAnimationFrame(frameId);
    }, [checkScrollButtons, mobile, reminder.length, priority.length]);

    return (
        <div className='activity-feed'>
                <div className='feed'>
                    <div className='feed-filters'>
                        {/*<button className={`action filters-button${activeFeed === 'All' ? ' active' : ''}`} onClick={() => setActiveFeed('All')}>
                            Filter <ChevronUp size={16} />
                        </button>*/}
                        <SearchBar placeholder="Search" expanded={expanded} setExpanded={setExpanded} setSearchQuery={setSearchQuery} />
                        <div className='filter-specifier filter-group'>
                            <div className='select-wrapper' style={{ width: '100%' }}>
                                <select className='filter-select' value={searchSpecifier} onChange={(e) => setSearchSpecifier(e.target.value)} style={{ width: '100%' }}>
                                    <option value='all'>Search All</option>
                                    <option value='subject'>Subject</option>
                                    <option value='content'>Content</option>
                                </select>
                                <ChevronDown size={16} className='select-chevron' />
                            </div>
                        </div>
                        <button 
                            className='action alt' 
                            onClick={exportToPDF}
                            title={`Export ${activeFeed} activities to PDF`}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                            <Download size={16} />
                            {!mobile && 'Export PDF'}
                        </button>
                        {hasLinkedChildCases && (
                            <button
                                className='action alt'
                                onClick={handleSyncFeed}
                                title={`Sync ${activeFeed} activities`}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                {!mobile && 'Sync Feed'}
                            </button>
                        )}
                        <div className="feed-types-container">
                            {showLeftScroll && (
                                <button 
                                    className="feed-scroll-button left"
                                    onClick={handleScrollLeft}
                                    aria-label="Scroll left"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                            )}
                            <div 
                                className={`feed-types${mobile ? ' mobile' : ''}`}
                                ref={feedTypesRef}
                                onScroll={checkScrollButtons}
                            >
                                <FeedItem type="All" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem type="Notes" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem type="Emails" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem type="Faxes" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem type="Phone Calls" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem type="Texts" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem type="Tasks" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem type="Forms" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                                <FeedItem
                                    type="Reminders"
                                    setActiveFeed={setActiveFeed}
                                    activeFeed={activeFeed}
                                    notifications={{
                                        reminder: reminder.length,
                                        priority: priority.length,
                                    }}
                                    mobile={mobile}
                                />
                                {/* <FeedItem type="eSignatures" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} /> */}
                            </div>
                            {showRightScroll && (
                                <button 
                                    className="feed-scroll-button right"
                                    onClick={handleScrollRight}
                                    aria-label="Scroll right"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                    <AddActivity fetchFeed={fetchFeed} lead_id={lead_id} case_id={case_id} onClick={() => setAddActivity(true)} setAddActivity={setAddActivity} addActivity={addActivity} users={users} contacts={contacts} initialDraft={composerDraft} createdContact={activityCreatedContact} onRequestCreateContact={() => setCreateContact(true)} onCancel={(result) => {
                        if (result?.settlement_cancelled) {
                            setComposerDraft(null);
                            onSettlementDraftCancelled?.(result);
                        }
                        if (result?.disengagement_cancelled) {
                            setComposerDraft(null);
                            onDisengagementDraftCancelled?.(result);
                        }
                    }} onActivityCreated={(result) => {
                        if (result?.settlement_phase_updated) {
                            setComposerDraft(null);
                            onSettlementDraftSubmitted?.(result);
                        }
                        if (result?.disengagement_phase_updated) {
                            setComposerDraft(null);
                            onDisengagementDraftSubmitted?.(result);
                        }
                    }} />
                    {createContact && <CreateContact setCreateContact={setCreateContact} fetchContacts={fetchContacts} onContactCreated={handleActivityContactCreated} />}
                    {loadingFeed && feed.length === 0 ? (
                        <div className='no-activity'>
                            <LoaderCircle className="spinner" />
                            <span className='subtext'>Loading Feed...</span>
                        </div>
                    ) : feed.length > 0 ? (
                        feedSections
                    ) : (
                        <div className='no-activity'>
                            <span className='subtext'>No feed items for this matter</span>
                        </div>
                    )}
            </div>
        </div>
    );
}
